import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { t } from 'i18next';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminOrderReportState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
}

const defaultColumnList = [
  {
    name: 'order',
    label_key: 'order',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: 'order_all',
    asyncKey: 'order',
    option_value_key: 'id',
    option_label_key: 'order_number',
    multi: false,
    order: 1,
  },
  {
    name: 'user',
    label_key: 'user',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('customer_group') }),
    asyncKey: 'customer_group',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 2,
  },
  {
    name: 'reported_by',
    label_key: 'reported_by',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'approval_status',
    label_key: 'status',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 4,
  },

  {
    name: 'reason',
    label_key: 'reason',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 5,
  },
  {
    name: 'remarks',
    label_key: 'remarks',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 6,
  },
  {
    name: 'created_at',
    label_key: 'created_at',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 7,
  },
  {
    name: 'ffl',
    label_key: 'ffl',
    allow_list: false,
    allow_filter: false,
    show_list_checkbox: false,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: 'ffl_all',
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 8,
  },
];

const defaultConfig = {
  allow_list: true,
  allow_filter: false,
  show_list_checkbox: true,
  show_filter_checkbox: false,
};

const fields = [
  { key: 'order_number', label: 'order_number' },
  { key: 'user_name', label: 'buyer' },
  { key: 'user_email', label: 'buyer_email' },
  { key: 'reported_by_name', label: 'reported_by' },
  { key: 'reported_by_email', label: 'reported_by_email' },

  { key: 'reason', label: 'reason' },
  { key: 'remarks', label: 'remarks' },
  { key: 'created_at', label: 'created_at' },
];

const exportColumnList = fields.map((field, index) => ({
  name: field.key,
  label_key: field.label,
  order: index + 1,
  ...defaultConfig,
}));

const initialState: AdminOrderReportState = {
  excludedFilter: ['search'],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 8,
    minFilterColumn: 0,
    maxFilterColumn: 8,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
};

const adminOrderReportSlice = createSlice({
  name: 'adminOrderReport',
  initialState,
  reducers: {
    setOrderReportFilterData(state, action: PayloadAction<Record<string, any>>) {
      state.filterData = action.payload;
    },
    removeOrderReportFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    resetOrderReportFilterData(state) {
      state.filterData = {};
    },
    setOrderReportReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setOrderReportTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      state.tableColumnList = action.payload;
    },
  },
});

export const {
  setOrderReportFilterData,
  removeOrderReportFilterKey,
  resetOrderReportFilterData,
  setOrderReportReloadTableData,
  setOrderReportTableColumnList,
} = adminOrderReportSlice.actions;

export default adminOrderReportSlice.reducer;
